import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { CheckCircle, FileText, Trash2, Plus, Info, Loader } from './Icons'; // Make sure path is correct based on your folder structure

export default function Sidebar({
  // 1. Static/Config Props
  logo,
  projectName,     
  setProjectName,   

  // 2. Progress State
  isExtracting,
  extractionStatus,
  extractionProgress,
  calculateOverallProgress,

  // 3. Navigation State
  activeItem,
  setActiveItem,

  // 4. Data State
  indexFile,
  sections,
  uploadedSections,
  skippedSections,

  // 5. Handlers
  handleOnDragEnd,
  handleSectionDelete,
  handleAddSection,

  // 6. Merge State & Handlers
  merging,
  mergedDocument,
  canMerge,
  handleMergeDocuments,

  sectionFiles = [], 
  onDeleteFile,
  onSaveProjectName,
  viewingFileUrl,
  onFileClick,
  jurisdiction,
  setJurisdiction,
}) {
  return (
    <aside className="sidebar">

      <div className="sidebar-header">
      <div className="sidebar-brand-area">
        <img src={logo} alt="PleadPerfect" className="sidebar-logo" />
      </div>
      <div className="project-name-container">
        <div className="project-name-label">Project Name</div>
        <input 
          type="text" 
          className="project-name-input"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="Untitled Project"
          onBlur={(e) => {
        // This triggers when you click OUTSIDE the box
        if(onSaveProjectName) {
            onSaveProjectName(e.target.value);
        }
    }}
        />
      </div>

        <div className="progress-container">
          <div className={`progress-bar ${isExtracting ? 'extracting' : ''}`}>
            <div
              className="progress-fill"
              style={{ width: `${calculateOverallProgress()}%` }}
            ></div>
          </div>

          <div className="progress-text">
            {isExtracting
              ? `${extractionStatus} (${extractionProgress}%)`
              : `${calculateOverallProgress()}% Complete`}
          </div>
        </div>
      </div>

      <div className="project-name-container">
      <div className="project-name-label">Jurisdiction</div>
      <input 
        type="text" 
        className="project-name-input"
        value={jurisdiction}
        onChange={(e) => setJurisdiction(e.target.value)}
        placeholder="e.g. Delhi HC"
        onBlur={(e) => {
          if (onSaveProjectName) onSaveProjectName(e.target.value);
        }}
      />
    </div>

      <div className="sidebar-content">
        <div className="list-group">
          <div className="list-title">SETUP</div>
          <div
            className={`list-item ${activeItem.type === 'index' ? 'active' : ''}`}
            onClick={() => setActiveItem({ type: 'index', index: null })}
          >
            <div className="item-icon-wrapper">
              {indexFile ? <CheckCircle size={20} className="icon-success" /> : <FileText size={20} />}
            </div>
            <div className="item-details">
              <div className="item-name">Master Index</div>
              <div className="item-status">{indexFile ? 'Uploaded' : 'Required'}</div>
            </div>
          </div>
        </div>

        {(skippedSections || []).length > 0 && (
          <div style={{
            margin: '12px 16px',
            padding: '12px',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Info size={16} />
              <strong>Documents already uploaded: ({(skippedSections || []).length})</strong>
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {(skippedSections || []).map((section, idx) => (
                <div key={idx}>• {section.name}</div>
              ))}
            </div>
          </div>
        )}

        {(sections || []).length > 0 && (
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="sections-list">
              {(provided) => (
                <div
                  className="list-group"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  <div className="list-title">SECTIONS ({(sections || []).length})</div>

                  {sections.map((section, idx) => {
                    const isUploaded = section.uploaded || !!uploadedSections[idx];
                    const isActive = activeItem.type === 'section' && activeItem.index === idx;

                    return (
                      <Draggable key={idx} draggableId={`section-${idx}`} index={idx}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            // Moved style here so the whole group (Section + Files) has spacing
                            style={{ ...provided.draggableProps.style, marginBottom: '8px' }}
                            className="section-group-wrapper" 
                          >
                            {/* 1. The Main Section Row (This is the Handle) */}
                            <div
                              {...provided.dragHandleProps} // Only this row is the "handle" for dragging
                              className={`list-item ${isActive ? 'active' : ''}`}
                              onClick={() => setActiveItem({ type: 'section', index: idx })}
                            >
                              <div className="item-icon-wrapper">
                                {isUploaded ? <CheckCircle size={20} className="icon-success" /> : <div className="item-number">{idx + 1}</div>}
                              </div>
                              <div className="item-details">
                                <div className="item-name">{section.name}</div>
                                <div className="item-status">{isUploaded ? 'Ready' : 'Pending'}</div>
                              </div>
                              {isUploaded && (
                                <button className="btn-delete" onClick={(e) => handleSectionDelete(e, idx)}>
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>

                            {/* 2. The File Submenu (Rides along with the section) */}
                            {isActive && isUploaded && sectionFiles && sectionFiles.length > 0 && (
  <div className="submenu-container">
    {sectionFiles.map((filename, fIdx) => {
      // 1. Clean up display name
      let displayName = filename;
      const parts = filename.split('_');
      if (parts.length > 3) displayName = parts.slice(3).join('_');
      
      const isFileSelected = viewingFileUrl && viewingFileUrl.includes(filename);

      return (
        <div 
          key={fIdx} 
          className={`submenu-item ${isFileSelected ? 'selected' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            console.log("Sidebar click triggered for:", filename); // Debug 1
            if (onFileClick) onFileClick(filename);
          }}
        >
          <div className="submenu-line"></div>
          <FileText size={12} className="submenu-icon"/>
          <span className="submenu-text" title={displayName}>{displayName}</span>
          <button 
            className="submenu-delete"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteFile(filename);
            }}
          >
            <Trash2 size={12} />
          </button>
        </div>
      );
    })}
  </div>
)}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}

                  <button
                    className="list-item btn-add-section"
                    onClick={handleAddSection}
                    style={{
                      marginTop: '8px',
                      justifyContent: 'center',
                      border: '1px dashed rgba(255,255,255,0.3)',
                      background: 'transparent',
                      color: 'rgba(255,255,255,0.7)',
                      cursor: 'pointer'
                    }}
                  >
                    <Plus size={18} />
                    <span style={{ marginLeft: '8px', fontSize: '13px' }}>Add Section</span>
                  </button>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <div className="sidebar-footer">
        <button
          className={`btn-merge ${mergedDocument ? 'success' : ''}`}
          disabled={!canMerge || merging}
          onClick={mergedDocument ? () => setActiveItem({ type: 'merged', index: null }) : handleMergeDocuments}
        >
          {mergedDocument ? (
            <>
              <CheckCircle size={20} />
              View Final PDF
            </>
          ) : (
            <>
              {merging ? <Loader size={20} /> : <FileText size={20} />}
              {merging ? 'Merging...' : 'Merge Documents'}
            </>
          )}
        </button>
      </div>
    </aside>
  );
}